import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CartModal from '../../cart/modal';
import MobileMenu from './mobile-menu';
import { UserProfileMenu } from './user-profile-menu';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage, type Lang } from '../../../context/LanguageContext';

const SHOP_LINKS = [
  { labelKey: 'nav.studio', path: '/studio' },
  { labelKey: 'nav.bespoke', path: '/bespoke' },
  { labelKey: 'nav.all_collections', path: '/collections' },
];

const BRAND_LINKS = [
  { labelKey: 'nav.about', path: '/about' },
  { labelKey: 'nav.documentary', path: '/documentary' },
  { labelKey: 'nav.press', path: '/press' },
];

function MenuDropdown({
  label,
  links,
  isOpen,
  onOpen,
  onClose,
  onEnterDropdownArea,
  onLeaveDropdownArea,
  t,
  useT,
}: {
  label: string;
  links: { labelKey?: string; label?: string; path: string }[];
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onEnterDropdownArea: () => void;
  onLeaveDropdownArea: () => void;
  t: (key: string) => string;
  useT: boolean;
}) {
  const handleMouseEnter = () => {
    onEnterDropdownArea();
    onOpen();
  };

  const handleMouseLeave = () => {
    onLeaveDropdownArea();
  };

  return (
    <div
      className="relative inline-block z-10"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span
        role="button"
        tabIndex={0}
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-haspopup="true"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
        className="font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors duration-200 cursor-pointer inline-block py-2"
      >
        {label}
      </span>
      {isOpen && (
        <div
          className="absolute left-0 top-full pt-1 z-50 pointer-events-auto"
          onMouseEnter={onEnterDropdownArea}
          onMouseLeave={onLeaveDropdownArea}
        >
          <div className="min-w-[220px] rounded-xl bg-[#0c0c0b]/95 backdrop-blur-2xl border border-neutral-800/40 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.35)] overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-neutral-800/50">
              <img
                src="/assets/images/maisonadjiwhite.png"
                alt=""
                className="h-6 w-auto object-contain shrink-0 opacity-95"
              />
              <span className="font-['Sora'] text-[11px] tracking-[0.28em] uppercase text-neutral-200 font-extralight">Maison Adji</span>
            </div>
            <ul className="py-1.5">
              {links.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={onClose}
                    className="block px-6 py-3.5 font-['Sora'] text-[11px] tracking-[0.2em] uppercase text-neutral-500 hover:text-neutral-100 hover:pl-7 border-l-2 border-transparent hover:border-neutral-500 transition-all duration-200"
                  >
                    {useT && link.labelKey ? t(link.labelKey) : (link.label ?? link.path)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function LanguagePicker() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options: { value: Lang; label: string }[] = [
    { value: 'fr', label: 'FR' },
    { value: 'en', label: 'EN' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white/60 hover:text-white transition-colors flex items-center gap-1"
        aria-label="Langue"
      >
        {lang.toUpperCase()}
        <svg className="w-2.5 h-2.5 opacity-50" fill="none" viewBox="0 0 10 6">
          <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-20 bg-neutral-900 border border-white/10 rounded-sm shadow-xl overflow-hidden z-50">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setLang(opt.value);
                setOpen(false);
              }}
              className={[
                'w-full text-left px-4 py-2.5 font-["Rubik"] text-xs tracking-widest uppercase transition-colors',
                lang === opt.value ? 'text-white bg-white/10' : 'text-white/50 hover:text-white hover:bg-white/5',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  const isDashboard = location.pathname.startsWith('/espace-business');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpenMenu(null), [location.pathname]);

  const [openMenu, setOpenMenu] = useState<'shop' | 'brand' | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    closeTimeoutRef.current = setTimeout(() => setOpenMenu(null), 200);
  };

  useEffect(() => () => clearCloseTimeout(), []);

  return (
    <header
      className={[
        'fixed top-0 inset-x-0 z-[100] transition-all duration-500',
        scrolled
          ? 'bg-neutral-950/90 backdrop-blur-md border-b border-white/10'
          : 'bg-transparent',
      ].join(' ')}
    >
      <nav className="relative flex items-center justify-between px-5 md:px-8 h-16 md:h-20">

        {/* ── Gauche : SHOP + THE BRAND (menus déroulants) ── */}
        {!isDashboard && (
          <>
            <div className="hidden md:flex items-center gap-8 flex-1">
              <MenuDropdown
                label={t('nav.shop')}
                links={SHOP_LINKS}
                isOpen={openMenu === 'shop'}
                onOpen={() => setOpenMenu('shop')}
                onClose={() => setOpenMenu(null)}
                onEnterDropdownArea={clearCloseTimeout}
                onLeaveDropdownArea={scheduleClose}
                t={t}
                useT={true}
              />
              <MenuDropdown
                label={t('nav.the_brand')}
                links={BRAND_LINKS}
                isOpen={openMenu === 'brand'}
                onOpen={() => setOpenMenu('brand')}
                onClose={() => setOpenMenu(null)}
                onEnterDropdownArea={clearCloseTimeout}
                onLeaveDropdownArea={scheduleClose}
                t={t}
                useT={true}
              />
            </div>
            <div className="flex md:hidden flex-1">
              <MobileMenu shopLinks={SHOP_LINKS} brandLinks={BRAND_LINKS} t={t} />
            </div>
          </>
        )}
        {isDashboard && <div className="flex-1" />}

        {/* ── Centre : Logo + texte "Maison ADJI" au survol ── */}
        <Link
          to="/"
          className="group absolute left-1/2 -translate-x-1/2 flex items-center gap-0"
          aria-label="Maison Adji — Accueil"
        >
          <img
            src="/assets/images/maisonadjiwhite.png"
            alt=""
            className="h-8 md:h-10 w-auto object-contain shrink-0"
          />
          <span className="block overflow-hidden max-w-0 whitespace-nowrap transition-[max-width] duration-500 ease-out group-hover:max-w-[11rem] md:group-hover:max-w-[12rem]">
            <span className="inline-block pl-2 md:pl-3 font-['Sora'] text-sm md:text-base font-light tracking-[0.2em] uppercase text-white/90 opacity-0 -translate-x-1 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-x-0">
              Maison ADJI
            </span>
          </span>
        </Link>

        {/* ── Droite : langue + panier + auth ── */}
        <div className="flex items-center justify-end gap-4 flex-1">
          <LanguagePicker />
          <CartModal />
          {user ? (
            <div className="hidden md:block">
              <UserProfileMenu />
            </div>
          ) : (
            <Link
              to="/login"
              className="hidden md:inline-flex font-['Rubik'] text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors duration-200"
            >
              {t('nav.login')}
            </Link>
          )}
        </div>

      </nav>
    </header>
  );
}
